"use client";

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import type { Socket } from "socket.io-client";
import { useUser } from "./UserContext";
import {
  createSocketConnection,
  WardrobeItemPayload,
  WardrobeItemDeletedPayload,
  BatchProgressPayload,
  ProcessingStepPayload,
  UploadItemReadyPayload,
  UploadItemFailedPayload,
  UploadJobCompletePayload,
  SetProcessingStepPayload,
  NotificationEventPayload,
} from "@/shared/lib/websocket";

/**
 * WebSocket Context for real-time updates
 */
interface WebSocketContextType {
  socket: Socket | null;
  isConnected: boolean;
  onWardrobeItemCreated: (callback: (item: WardrobeItemPayload) => void) => () => void;
  onWardrobeItemDeleted: (callback: (payload: WardrobeItemDeletedPayload) => void) => () => void;
  onBatchProgress: (callback: (payload: BatchProgressPayload) => void) => () => void;
  onProcessingStep: (callback: (payload: ProcessingStepPayload) => void) => () => void;
  onUploadItemReady: (callback: (payload: UploadItemReadyPayload) => void) => () => void;
  onUploadItemFailed: (callback: (payload: UploadItemFailedPayload) => void) => () => void;
  onUploadJobComplete: (callback: (payload: UploadJobCompletePayload) => void) => () => void;
  onSetProcessingStep: (callback: (payload: SetProcessingStepPayload) => void) => () => void;
  onNotificationNew: (callback: (payload: NotificationEventPayload) => void) => () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(undefined);

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (context === undefined) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
}

interface WebSocketProviderProps {
  children: ReactNode;
}

export function WebSocketProvider({ children }: WebSocketProviderProps) {
  const { userId, isAuthenticated } = useUser();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Connect when user is authenticated
  useEffect(() => {
    if (!isAuthenticated || !userId) {
      // Disconnect if not authenticated
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Create connection. createSocketConnection is now async (it lazy-loads
    // socket.io-client), so guard against the effect being cleaned up before the
    // socket resolves — otherwise a stale socket would leak past unmount.
    let cancelled = false;
    let activeSocket: Socket | null = null;
    createSocketConnection(userId).then((newSocket) => {
      if (cancelled) {
        newSocket.disconnect();
        return;
      }
      activeSocket = newSocket;
      setSocket(newSocket);
      newSocket.on("connect", () => setIsConnected(true));
      newSocket.on("disconnect", () => setIsConnected(false));
    });

    // Cleanup on unmount or userId change
    return () => {
      cancelled = true;
      if (activeSocket) activeSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, userId]);

  /**
   * Subscribe to wardrobe:item-created events
   * Returns an unsubscribe function
   */
  const onWardrobeItemCreated = useCallback(
    (callback: (item: WardrobeItemPayload) => void) => {
      if (!socket) {
        return () => {};
      }

      socket.on("wardrobe:item-created", callback);

      return () => {
        socket.off("wardrobe:item-created", callback);
      };
    },
    [socket]
  );

  /**
   * Subscribe to wardrobe:item-deleted events
   * Returns an unsubscribe function
   */
  const onWardrobeItemDeleted = useCallback(
    (callback: (payload: WardrobeItemDeletedPayload) => void) => {
      if (!socket) {
        return () => {};
      }

      socket.on("wardrobe:item-deleted", callback);

      return () => {
        socket.off("wardrobe:item-deleted", callback);
      };
    },
    [socket]
  );

  /**
   * Subscribe to wardrobe:batch-progress events
   * Returns an unsubscribe function
   */
  const onBatchProgress = useCallback(
    (callback: (payload: BatchProgressPayload) => void) => {
      if (!socket) {
        return () => {};
      }

      socket.on("wardrobe:batch-progress", callback);

      return () => {
        socket.off("wardrobe:batch-progress", callback);
      };
    },
    [socket]
  );

  /**
   * Subscribe to wardrobe:processing-step events
   * Returns an unsubscribe function
   */
  const onProcessingStep = useCallback(
    (callback: (payload: ProcessingStepPayload) => void) => {
      if (!socket) {
        return () => {};
      }

      socket.on("wardrobe:processing-step", callback);

      return () => {
        socket.off("wardrobe:processing-step", callback);
      };
    },
    [socket]
  );

  const onUploadItemReady = useCallback(
    (callback: (payload: UploadItemReadyPayload) => void) => {
      if (!socket) return () => {};
      socket.on("upload:item-ready", callback);
      return () => { socket.off("upload:item-ready", callback); };
    },
    [socket]
  );

  const onUploadItemFailed = useCallback(
    (callback: (payload: UploadItemFailedPayload) => void) => {
      if (!socket) return () => {};
      socket.on("upload:item-failed", callback);
      return () => { socket.off("upload:item-failed", callback); };
    },
    [socket]
  );

  const onUploadJobComplete = useCallback(
    (callback: (payload: UploadJobCompletePayload) => void) => {
      if (!socket) return () => {};
      socket.on("upload:job-complete", callback);
      return () => { socket.off("upload:job-complete", callback); };
    },
    [socket]
  );

  const onSetProcessingStep = useCallback(
    (callback: (payload: SetProcessingStepPayload) => void) => {
      if (!socket) return () => {};
      socket.on("set:processing-step", callback);
      return () => { socket.off("set:processing-step", callback); };
    },
    [socket]
  );

  const onNotificationNew = useCallback(
    (callback: (payload: NotificationEventPayload) => void) => {
      if (!socket) return () => {};
      socket.on("notification:new", callback);
      return () => { socket.off("notification:new", callback); };
    },
    [socket]
  );

  const value: WebSocketContextType = {
    socket,
    isConnected,
    onWardrobeItemCreated,
    onWardrobeItemDeleted,
    onBatchProgress,
    onProcessingStep,
    onUploadItemReady,
    onUploadItemFailed,
    onUploadJobComplete,
    onSetProcessingStep,
    onNotificationNew,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}
